<template>
  <component
    :is="tag"
    class="m-link"
    :class="{
      'm-link--icon': iconLeft || iconRight,
      'm-link--chevron': linkStyle && linkStyle.includes('chevron'),
      'm-link--block': block,
      [`m-link--${linkStyle}`]: !!linkStyle,
      [`m-link--${size}`]: !!size,
      [`m-link--color-${color}`]: !!color,
    }"
    :disabled="isDisabled || null"
    v-bind="attrs"
  >
    <MIcon
      v-if="iconLeft"
      :icon="iconLeft"
      class="m-link__icon m-link__icon--left"
    /><slot /><MIcon
      v-if="iconRight"
      :icon="iconRight"
      class="m-link__icon m-link__icon--right"
    />
    <!-- slot has to be in line with icons to prevent a compiler from adding a white space -->
  </component>
</template>

<script>
import { cloneDeep } from 'lodash-es'

import { clickableTag } from '../../../lib/type-validators'
import { getLinkAttrs } from '../../../lib/utils'
import MIcon from '../../typography/Icon/MIcon'

/** Visually looks like a link but can be used semantically as a button too, depending on the action. */
export default {
  name: 'MLink',
  components: { MIcon },
  inheritAttrs: false,
  props: {
    /**
     * HTML tag for the component
     */
    tag: {
      type: [Object, String],
      default: 'a',
      validator: clickableTag,
    },
    /**
     * Change link style
     */
    linkStyle: {
      type: String,
      default: null,
      validator: (prop) => ['underline', 'no-underline'].includes(prop),
    },
    /**
     * Change default size
     */
    size: {
      type: String,
      default: null,
      validator: (prop) => ['small', 'large'].includes(prop),
    },
    /**
     * Disabled state
     */
    isDisabled: {
      type: Boolean,
      default: null,
    },
    /**
     * Icon on the left
     */
    iconLeft: {
      type: String,
      default: null,
    },
    /**
     * Icon on the right
     */
    iconRight: {
      type: String,
      default: null,
    },
    /**
     * Inherit the current text color
     */
    color: {
      type: String,
      default: null,
      validator: (prop) => ['inherit'].includes(prop),
    },
    /**
     * Display as 'block' to span full width and center text,
     * although clickable area is limited to text contents
     */
    block: {
      type: [Boolean],
      default: null,
      validator: (prop) => [true, false].includes(prop),
    },
  },
  computed: {
    attrs() {
      const clonedAttrs = cloneDeep(this.$attrs)

      // Button should not allow interactions, if it's disabled
      if (this.isDisabled && clonedAttrs.onClick) {
        delete clonedAttrs.onClick // Get rid of the click listeners, if button should be unclickable
      }

      return getLinkAttrs(clonedAttrs, this.tag)
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../../sass/framework/mixins/links';
@use '../../../sass/framework/mixins/font-size';

.m-link {
  @include links.link-base;

  &__icon {
    &--left {
      margin-right: var(--spacing-4);
    }
    &--right {
      margin-left: var(--spacing-4);
    }
  }

  &--block {
    display: block;
    margin: auto;
    max-width: fit-content; // Prevent spanning entire width, rendering clickable whitespace
  }

  &--underline {
    @include links.link;
  }

  &--no-underline {
    @include links.link-no-underline;
  }

  &--icon {
    align-items: center;
    display: inline-flex;
    justify-content: center;
  }

  &--small {
    @include font-size.font-size(sm);
  }

  &--large {
    @include font-size.font-size(lg);
  }

  &--color {
    &-inherit {
      color: inherit;
    }
  }
}
</style>
